import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable, reaction} from "mobx";
import {RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IRoundsStore, ISession} from "data/stores/rounds/rounds.store";
import {SyntheticEvent} from "react";

export interface ITeamSelectorController extends ViewController {
	get i18n(): ILocalizationStore;
	get golfers(): number[];
	get captain(): number;
	get selectedRoundSessions(): ISession[];
	get selectedSessionId(): number;
	get isCurrentLineupFull(): boolean;
	get isCurrentLineupHasPlayer(): boolean;

	onChangeSession: (_event: SyntheticEvent, newValue: string) => void;
}

@injectable()
export class TeamSelectorController implements ITeamSelectorController {
	@observable _requestState: RequestState = RequestState.IDLE;

	private _disposerReactionSessionPMActive!: ReturnType<typeof reaction>;

	get i18n() {
		return this._i18nStore;
	}

	get golfers() {
		return this._teamStore.lineup.golfers;
	}

	get captain() {
		return this._teamStore.lineup.captain;
	}

	get selectedRoundSessions() {
		return this._roundStore.selectedRoundSessions;
	}

	get selectedSessionId() {
		return this._roundStore.selectedSessionId;
	}
	get isCurrentLineupFull() {
		return this._teamStore.isCurrentLineupFull;
	}

	get isCurrentLineupHasPlayer() {
		return this._teamStore.isCurrentLineupHasPlayer;
	}

	private get isReadyToChangeSession() {
		return this._roundStore.isPMSessionNotScheduled && this._teamStore.isCurrentLineupHasPlayer;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore
	) {
		makeAutoObservable(this);
	}

	onChangeSession = (_event: SyntheticEvent, newValue: string) => {
		this._roundStore.setSelectedSessionId(Number(newValue));
	};

	dispose() {
		this._disposerReactionSessionPMActive?.();
	}

	init() {
		this._disposerReactionSessionPMActive = reaction(
			() => this.isReadyToChangeSession,
			(isReadyToChangeSession) => {
				if (isReadyToChangeSession) {
					this._roundStore.setSelectedSessionId(
						this._roundStore.selectedRoundSessions[1].id ?? 0
					);
				}
			},
			{fireImmediately: true}
		);
	}
}
